import React, {
    useRef,
    useState,
    useLayoutEffect,
    useCallback,
    ReactNode,
    useEffect,
    useSyncExternalStore,
} from 'react'
import { PanelContextProvider } from 'refactor/hooks/usePanelContext'
import { fixedHeaderEnabled } from 'lib/fixedHeader'
import { keyboardEdgeActive } from 'lib/keyboardEdgeAdapter'
import PanelPlaceBar from 'components/panels/PanelPlaceBar'
import { isMobile, isAndroid, isIOS } from 'lib/device'
import {
    applyGlideStep,
    initialGlideState,
    resolveGlide,
    type GlideState,
    type KeyboardPhase,
} from 'lib/bottomEditorGlide'
import cn from 'classnames'
import styles from 'components/panels/panel-container.module.scss'
import {
    getCenterIndex,
    isPanelCenter,
    isPanelLeft,
    isPanelRight,
} from 'state/imperativeApis/swiperApi'
import { SMALL, BIG, transformTo, getTransform } from 'lib/transform'
import useStore, { DEBUG, PanelLastAction } from 'state/knovStore'
import useOpenRight from 'refactor/hooks/useOpenRight'
import { findAnswerTargetElement, getAnswerTargetScrollTop } from 'lib/answerTargetScroll'
import {
    measurePostedAnswerVisibility,
    planBottomEditorPostedAnswer,
    planPostedAnswerFeedback,
    readBottomEditorTop,
    resolvePostedAnswerRevealScrollTop,
    resolveReadingThreadEnd,
} from 'lib/postedAnswerFeedback'
import { PanelState } from 'state/PanelState'
import { OPEN_PLACEMENT_HIDDEN_STYLE } from 'lib/threadOpenPlacement'
import { installThreadOpenAnchor } from 'lib/threadOpenReveal'
import { prepareOpenRight } from 'refactor/panels/prepareOpenRight'
import PanelLoadingIndicator from './PanelLoadingIndicator'
import RemoveButton from './RemoveButton'
import FloatingFilterIcon from './FloatingFilterIcon'
import { resolvePanelBottomSpacers } from 'lib/panelBottomSpacer'
import NewItemsPill from './NewItemsPill'
import useFeedNewItems from './useFeedNewItems'
import { refreshRadioStreamQuery } from './refreshRadioStreamQuery'
import { isRadioStreamFilter } from './feedNewItemsEligibility'
import { useMusicPanelView } from './musicPanelView'
import useOrdinalStreamUpdates from './useOrdinalStreamUpdates'
import useThreadNewReplies from './useThreadNewReplies'
import queryClient from 'api/queryClient'
import { getQuestsQueryKey, serializeFilterKey } from 'refactor/hooks/api/useGetQuests'
import { logEv } from 'lib/log'
import PullToRefresh from './PullToRefresh'
import MobilePullToRefresh from './MobilePullToRefresh'
import {
    activeRefreshState,
    idleRefreshState,
    resolveRefreshPresentation,
    updateHeaderRefreshState,
    type RefreshSource,
} from 'lib/refreshRedesign'
import WithTooltip from 'components/shared/WithTooltip'
import { withDepth } from 'components/filters/useStreamFilters'
import { ordinalsStreamQueryFilter } from 'components/panels/ordinalsThreshold'
import { getCachedQuest } from 'state/cache'
import {
    getSwiperRef,
    slideTo as slideToPanel,
    SWIPE_SPEED_SLOW,
} from 'state/imperativeApis/swiperApi'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import {
    dockActiveMediaPlaybackIfOutsideVisibleArea,
    getActiveMediaPlaybackVisibilityElement,
    subscribeToMediaDock,
} from 'state/mediaPlaybackCoordinator'
import { naturalFeedLayoutEnabled, naturalThreadLayoutEnabled } from 'lib/mediaBuckets'
import { resetNaturalFeedSessionSnapshot } from 'lib/naturalFeedSession'
import { shouldKeepPanelContentActive } from 'lib/panelContentActivity'
import { retainDecodedPanelMedia } from 'lib/panelMediaWarmCache'
import {
    getPanelNavInputDetail,
    markPanelNavMetric,
    startPanelNavMetric,
} from 'lib/panelNavMetrics'
import { hasPanelInDirection, playPanelBoundaryFeedback } from 'lib/panelBoundaryFeedback'
import {
    getBottomControlOffset,
    readTransformTranslateY,
    resolveNavigationTop,
    bottomControlsSlideY,
    readTranslatePropertyY,
} from 'lib/bottomControlGeometry'
import {
    BOTTOM_EDITOR_FIELD_MAX_HEIGHT_VAR,
    BOTTOM_EDITOR_NAVIGATION_CLEARANCE_VAR,
    canMeasureBottomEditorField,
    isBottomEditorResize,
    resolveBottomEditorFieldMaxHeight,
} from 'lib/bottomEditor'
import { getBottomEditorDock, resolveRetainedClearance } from 'lib/bottomEditorDock'
import {
    calculatePanelEndGutterHeight,
    getPanelScrollBottomInset,
    getPanelScrollTopInset,
    PANEL_SCROLL_BOTTOM_OBSTRUCTION_SELECTOR,
    PANEL_SCROLL_END_ANCHOR_SELECTOR,
    PANEL_SCROLL_ROW_SELECTOR,
} from 'lib/panelEndGutter'
import {
    consumeSpaceNotificationBadge,
    consumeSpaceNotificationBadgeIfVersionMatches,
    getSpaceNotificationBadgeEventVersion,
} from 'refactor/hooks/api/notificationBadgeRefetchPolicy'

export interface PanelContainerProps {
    panel: PanelState
    index: number
    hide?: boolean
    isEmpty?: boolean
    children: ReactNode
    panelTheme: string
    keyboardOpen?: boolean
    keyboardHeight?: number
    // The keyboard has just gone: the navbar is rising back and the bottom
    // editor's foot glides up onto it over the same window.
    keyboardSettling?: boolean
    // What the keyboard is doing, for the pill's glide (lib/bottomEditorGlide).
    keyboardPhase?: KeyboardPhase
    // The last keyboard's height: where the pill heads while one is expected.
    rememberedKeyboardHeight?: number
    // Whether the layout viewport shrinks for the keyboard (viewportResizesForKeyboard).
    nativeKeyboardResize?: boolean
    onBottomEditorPresenceChange?: (panelId: PanelState['panelId'], mounted: boolean) => void
    // What the bottom editor clears at a phone's bottom edge (a CSS length).
    bottomEditorInset?: string
    // Match PanelController's mobile-surface policy. Native wrappers can keep
    // the phone navbar even when their viewport is wider than the CSS breakpoint.
    bottomEditorMobileSurface?: boolean
    // The signed-in navbar is on screen. A phone's pill stands on it, so the
    // panel foot clears its measured height rather than the bare inset.
    bottomNavigationVisible?: boolean
    // bottom_editor_dock is on: a thread's phone pill is placed by the page's
    // dock (lib/bottomEditorDock), and the keyboard never moves the thread.
    dock?: boolean
}

// A held bottom-editor reserve is re-evaluated each time this much more of it
// leaves or enters the reader's view.
const RESERVE_RELEASE_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1]

// The jump chip's wait after a keyboard leaves a docked thread, when the
// thread's end does not come back into view by itself (a reader who really is
// away from the bottom).
const CHIP_KEYBOARD_SETTLE_MS = 400
const subscribeToNoDock = () => () => {}

// The offered trip after posting: long enough to notice and take, short
// enough that an ignored pill gets out of the way on its own.
// The posted row is appended as the write commits, so the decision waits for
// it to mount. Bounded: a row that never arrives is offered rather than lost.
const POSTED_ANSWER_MOUNT_POLL_MS = 60
const POSTED_ANSWER_MOUNT_ATTEMPTS = 16
const ANSWER_JUMP_OFFER_HOLD_MS = 5000
// Long enough for the shrink to finish before the offer state clears.
const ANSWER_JUMP_OFFER_EXIT_MS = 300
// On mobile the modal's keyboard is still collapsing when the post commits,
// which moves the very edge the control is anchored to. Let it settle before
// the morph starts — that also makes the visibility measurement honest.
const POSTED_ANSWER_KEYBOARD_SETTLE_MS = 400
// The bottom editor's pill shrinks to its emptied height over this long after
// a post (NewAnswer COMPACT_COMPOSER_RESIZE_MS); its top edge is where the
// reader's view of the thread ends.
const POSTED_ANSWER_BOTTOM_EDITOR_SETTLE_MS = 240
// How far short of the thread's end the reader still counts as at it.
const THREAD_END_SLACK_PX = 20

const PANEL_CONTENT_DEACTIVATION_DELAY_MS = SWIPE_SPEED_SLOW + 100
const OPEN_PLACEMENT_PAINT_GATE_TIMEOUT_MS = 9000
const OPEN_PLACEMENT_CANCEL_KEYS = new Set([
    'ArrowDown',
    'ArrowUp',
    'End',
    'Home',
    'PageDown',
    'PageUp',
    ' ',
])

type OpenPlacementPaintGate = {
    routeKey: string
    questId: string | null
    answerId: string | null
    intent: PanelState['pendingOpenPlacement']
    revealed: boolean
    failure?: 'timeout' | 'unavailable' | 'error'
}

export default React.memo(function PanelContainer({
    panel,
    index,
    hide,
    children,
    panelTheme,
    keyboardOpen,
    keyboardHeight,
    keyboardSettling = false,
    keyboardPhase = 'none',
    rememberedKeyboardHeight = 0,
    nativeKeyboardResize = isAndroid,
    onBottomEditorPresenceChange,
    bottomEditorInset,
    bottomEditorMobileSurface = isMobile,
    bottomNavigationVisible = false,
    dock = false,
}: PanelContainerProps) {
    // Only subscribe to panel changes for this specific panel
    const panelContainerRef = useRef<HTMLDivElement>(null)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const scrollTopSentinelRef = useRef<HTMLDivElement>(null)
    const scrollBottomSentinelRef = useRef<HTMLDivElement>(null)
    const transformContainerRef = useRef<HTMLDivElement>(null)
    const fallbackOpenAnchorRef = useRef<(() => void) | null>(null)
    const threadBottomGutterRef = useRef<HTMLDivElement>(null)
    // Starts true so the chip never flashes before the observer's first callback.
    const [threadAtBottom, setThreadAtBottom] = useState(true)
    // Editors (answer edit mode, composer focus) suppress the bottom chip so it
    // never floats over an open editing surface.
    const [externalHideBottomChip, setExternalHideBottomChip] = useState(false)
    // Posting from the reply modal used to yank the reader to the new post.
    // Instead the panel offers the trip: a pill rises, waits, and retreats
    // unanswered — the scroll happens only if the user takes it.
    const [answerJumpOffer, setAnswerJumpOffer] = useState<{
        answerId: string
        questId: string
        nonce: number
        // 'scroll' — the reply landed in the thread this panel shows, so the
        // trip is a reveal in place. 'open' — it landed somewhere this panel
        // is not (a stream card's thread), so the trip opens that thread.
        kind: 'scroll' | 'open'
        // Whether a chip was on screen when the offer was raised. With one the
        // pill grows out of it; without one it rises from the edge instead.
        fromHidden: boolean
    } | null>(null)
    const [answerJumpRetreating, setAnswerJumpRetreating] = useState(false)
    const answerJumpNonceRef = useRef(0)
    const currentQuestId = String(panel.filter?.questId || '')

    const { openRight } = useOpenRight(panel.panelId)
    const [bottomEditorMounted, setBottomEditorMountedState] = useState(false)
    const setBottomEditorMounted = useCallback(
        (mounted: boolean) => {
            setBottomEditorMountedState(mounted)
            onBottomEditorPresenceChange?.(panel.panelId, mounted)
        },
        [onBottomEditorPresenceChange, panel.panelId],
    )
    // bottom_editor: the thread portals its composer into this host, so the
    // pill is fixed to the panel's foot at every scroll position instead of
    // travelling with the card. State as well as a ref: the thread renders
    // into the node, so it has to re-render once the node exists.
    const bottomEditorHostRef = useRef<HTMLDivElement | null>(null)
    const [bottomEditorHost, setBottomEditorHost] = useState<HTMLDivElement | null>(null)
    // The host is two layers: the frame (this ref) wears the spacer as padding
    // and holds the panel's edge; the glide inside it is what the thread
    // portals the composer into, and what moves (lib/bottomEditorGlide).
    const attachBottomEditorHost = useCallback((node: HTMLDivElement | null) => {
        bottomEditorHostRef.current = node
    }, [])
    const attachBottomEditorGlide = useCallback((node: HTMLDivElement | null) => {
        setBottomEditorHost(node)
    }, [])
    const bottomEditorGlideStateRef = useRef<GlideState>(initialGlideState())
    const threadAtBottomRef = useRef(true)
    // The reader is reading the thread's end: it came into view and the thread
    // has not moved up since (resolveReadingThreadEnd). A post from the bottom
    // editor then follows the thread's end, even with the end out of view
    // under the keyboard, or behind the reply just appended.
    const readingEndRef = useRef(false)
    const endVisibleRef = useRef(false)
    // Where the thread stood when its end was last in view.
    const endSeenScrollTopRef = useRef<number | null>(null)
    useEffect(() => {
        threadAtBottomRef.current = threadAtBottom
    }, [threadAtBottom])
    const lastBottomEditorClearanceRef = useRef<{
        panelId: PanelState['panelId']
        layoutClearance: number
    } | null>(null)
    // The pill's field cap, measured at once (syncBottomEditorClearance's
    // settleBottomEditorFieldCap): an unfold asks for it before it measures the
    // size its motion aims at (NewAnswer).
    const measureBottomEditorFieldRef = useRef<(() => void) | null>(null)
    const measureBottomEditorField = useCallback(() => measureBottomEditorFieldRef.current?.(), [])
    const postedFeedbackTimerRef = useRef<number | null>(null)
    const clearPostedFeedbackTimer = useCallback(() => {
        if (postedFeedbackTimerRef.current == null) return
        window.clearTimeout(postedFeedbackTimerRef.current)
        postedFeedbackTimerRef.current = null
    }, [])
    useEffect(() => clearPostedFeedbackTimer, [clearPostedFeedbackTimer])

    const chipWouldShowRef = useRef(false)
    const raiseAnswerJumpOffer = useCallback(
        (answerId: string, questId: string, kind: 'scroll' | 'open') => {
            answerJumpNonceRef.current += 1
            setAnswerJumpRetreating(false)
            setAnswerJumpOffer({
                answerId,
                questId,
                nonce: answerJumpNonceRef.current,
                kind,
                fromHidden: !chipWouldShowRef.current,
            })
        },
        [],
    )

    // Where the new reply landed chooses the feedback: a reply the reader can
    // already see just flashes in place, and only one below the fold is worth
    // a pill offering the trip. From the bottom editor, a reply seen only in
    // part is also brought the rest of the way into view.
    const offerAnswerJump = useCallback(
        (
            answerId?: string | null,
            questId?: string | null,
            {
                fromBottomEditor = false,
                viaModal = false,
            }: { fromBottomEditor?: boolean; viaModal?: boolean } = {},
        ) => {
            if (!answerId || !questId) return
            const postedAnswerId = String(answerId)
            const postedQuestId = String(questId)
            // A second post supersedes the first one's pending decision.
            clearPostedFeedbackTimer()
            // Posted from the thread's end, the thread follows the post there.
            const followEnd = fromBottomEditor && readingEndRef.current

            const sourceQuestId = String(panel.filter?.questId || '')
            let attempts = 0
            const decide = () => {
                postedFeedbackTimerRef.current = null
                // The panel may have moved on to another thread while we waited.
                if (String(panel.filter?.questId || '') !== sourceQuestId) return

                // Stream offers share the keyboard-settle delay: their bottom
                // edge moves too, even though there is no row here to measure.
                if (sourceQuestId !== postedQuestId) {
                    raiseAnswerJumpOffer(postedAnswerId, postedQuestId, 'open')
                    return
                }

                const scrollContainer = panel.getScrollContainer?.()
                const row = scrollContainer
                    ? findAnswerTargetElement(scrollContainer, postedAnswerId)
                    : null

                if (!row || !scrollContainer) {
                    if (attempts++ < POSTED_ANSWER_MOUNT_ATTEMPTS) {
                        postedFeedbackTimerRef.current = window.setTimeout(
                            decide,
                            POSTED_ANSWER_MOUNT_POLL_MS,
                        )
                        return
                    }
                    raiseAnswerJumpOffer(postedAnswerId, postedQuestId, 'scroll')
                    return
                }

                // The bottom editor floats over the thread's foot, so the
                // reader sees the thread only above it. A reply shown in part
                // scrolls until it is whole, or until its top stands where an
                // answer link would put it.
                if (fromBottomEditor) {
                    const visibility = measurePostedAnswerVisibility(
                        scrollContainer,
                        row,
                        readBottomEditorTop(bottomEditorHostRef.current),
                    )
                    const plan = planBottomEditorPostedAnswer(visibility, { followEnd })
                    if (plan === 'offer') {
                        raiseAnswerJumpOffer(postedAnswerId, postedQuestId, 'scroll')
                        return
                    }
                    setAnswerJumpOffer(null)
                    setAnswerJumpRetreating(false)
                    if (plan === 'reveal') {
                        scrollContainer.scrollTo({
                            top: resolvePostedAnswerRevealScrollTop({
                                scrollTop: scrollContainer.scrollTop,
                                visibility,
                                answerTopScrollTop: getAnswerTargetScrollTop(scrollContainer, row),
                            }),
                            behavior: 'smooth',
                        })
                    }
                    panel.highlightAnswer?.(postedAnswerId)
                    return
                }

                const feedback = planPostedAnswerFeedback(
                    measurePostedAnswerVisibility(scrollContainer, row),
                )
                if (feedback === 'highlight') {
                    // The latest post replaces any older offer even when it
                    // needs only a flash, not another trip.
                    setAnswerJumpOffer(null)
                    setAnswerJumpRetreating(false)
                    panel.highlightAnswer?.(postedAnswerId)
                    return
                }
                raiseAnswerJumpOffer(postedAnswerId, postedQuestId, 'scroll')
            }

            // A bottom editor post waits for the pill to settle to its
            // emptied height, where the reader's view of the thread ends;
            // mobile waits for the keyboard to finish collapsing — a modal's
            // too, when the bottom editor was opened out into one.
            const keyboardSettleMs = isMobile ? POSTED_ANSWER_KEYBOARD_SETTLE_MS : 0
            const settleMs = fromBottomEditor
                ? Math.max(POSTED_ANSWER_BOTTOM_EDITOR_SETTLE_MS, viaModal ? keyboardSettleMs : 0)
                : keyboardSettleMs
            if (settleMs > 0) {
                postedFeedbackTimerRef.current = window.setTimeout(decide, settleMs)
                return
            }
            decide()
        },
        [clearPostedFeedbackTimer, panel, raiseAnswerJumpOffer],
    )

    // Deliberately does NOT clear the pending decision: an offer on screen has
    // already finished its own poll, so the only timer alive here belongs to a
    // newer post, whose feedback this dismissal must not swallow. Superseding
    // is offerAnswerJump's job, and unmount is the effect's.
    const dismissAnswerJump = useCallback(() => {
        setAnswerJumpOffer(null)
        setAnswerJumpRetreating(false)
    }, [])

    // Hold, then retreat. Keyed on the nonce so a second post restarts the
    // hold rather than inheriting the first one's remaining time.
    useEffect(() => {
        if (!answerJumpOffer) return
        const holdTimer = setTimeout(() => setAnswerJumpRetreating(true), ANSWER_JUMP_OFFER_HOLD_MS)
        return () => clearTimeout(holdTimer)
    }, [answerJumpOffer?.nonce])

    useEffect(() => {
        if (!answerJumpRetreating) return
        const exitTimer = setTimeout(dismissAnswerJump, ANSWER_JUMP_OFFER_EXIT_MS)
        return () => clearTimeout(exitTimer)
    }, [answerJumpRetreating, dismissAnswerJump])

    // An 'open' offer belongs to a thread this panel never showed, so it is
    // pinned to the panel that raised it rather than to a quest id.
    const offerIsValidHere = Boolean(
        answerJumpOffer &&
            (answerJumpOffer.kind === 'open' || answerJumpOffer.questId === currentQuestId),
    )

    // PanelState objects survive route reuse. Do not let an offer created in
    // one thread reappear over whichever thread later occupies this panel.
    useEffect(() => {
        if (!answerJumpOffer || offerIsValidHere) return
        dismissAnswerJump()
    }, [answerJumpOffer, offerIsValidHere, dismissAnswerJump])

    const takeAnswerJump = useCallback(() => {
        const offer = answerJumpOffer
        dismissAnswerJump()
        if (!offer) return

        if (offer.kind === 'open') {
            // openRightCore already owns the panel insert, the reveal, the
            // highlight and the right-panel dedupe — the same call the stream
            // reply used to make at post time, made now only if asked for.
            void openRight({ questId: offer.questId, answerId: offer.answerId })
            return
        }

        if (offer.questId !== currentQuestId) return

        // Taking the offer is navigation intent. Retarget the filter as well
        // as requesting the reveal so a missing row is refetched and pinned
        // against virtualization, matching answer permalink navigation.
        useStore.getState().panels.editPanel(panel.panelId, {
            filter: { ...(panel.filter || {}), answerId: offer.answerId },
        })
        void panel.requestAnswerReveal?.(offer.answerId, {
            questId: offer.questId,
            highlight: true,
            shake: true,
        })
    }, [answerJumpOffer, currentQuestId, dismissAnswerJump, openRight, panel])
    const offerActive = Boolean(answerJumpOffer) && offerIsValidHere

    // Both floating pills ride the same edge. The jump-to-latest chip and the
    // post-reply offer stand in for each other — only one is ever up — so they
    // must clear the same obstructions: the home indicator on a notched iPhone,
    // Treechat's navbar and the browser's own chrome on Android. Computed once
    // and applied to both; a private copy is how the offer ended up 27px lower
    // than the chip, sitting under Control.
    const bottomPillEdgeClass = React.useMemo(() => {
        if (!isMobile) return undefined
        if (isIOS) {
            // iOS: float above Control by +70 (with safe-area awareness)
            try {
                const { screen } = window
                const longestSide = Math.max(screen?.width || 0, screen?.height || 0)
                const hasHomeIndicator = longestSide >= 812
                return hasHomeIndicator
                    ? styles.iosBottomChipAboveControlNotch
                    : styles.iosBottomChipAboveControl
            } catch {
                return styles.iosBottomChipAboveControl
            }
        }
        if (isAndroid) return styles.androidBottomChipAboveNavigation
        return undefined
    }, [])
    const removeButtonContainerRef = useRef<HTMLDivElement>(null)
    const floatingFilterRef = useRef<HTMLDivElement>(null)
    const floatingHeaderNavLeftRef = useRef<HTMLDivElement>(null)
    const floatingHeaderNavRightRef = useRef<HTMLDivElement>(null)
    const numPanels = useStore(state => state.numPanels)
    const onePanel = numPanels === 1
    const showSidebar = useStore(state => state.showSidebar)
    const numNoti = useStore(state => (panel.filter?.notifications ? state.numNoti : 0))
    const activeIndex = useStore(state => (isMobile ? null : state.activeIndex))
    const panelActiveIndex = useStore(state => state.activeIndex)
    const onePanelPrewarmIndex = useStore(state => state.onePanelPrewarmIndex)
    const onePanelPrewarmVisible = useStore(state => state.onePanelPrewarmVisible)
    const panelsLength = useStore(state => state.panels.state?.length)
    const [canSlidePrev, setCanSlidePrev] = useState(false)
    const [canSlideNext, setCanSlideNext] = useState(false)
    // fixed_header frames each desktop panel (one-panel: a full-height column;
    // three-panel: a floating window). The side windows scale down as whole
    // windows (data-panel-side), so the content's own transform layer is off.
    const panelFrame: 'column' | 'window' | undefined =
        fixedHeaderEnabled() && !isMobile ? (onePanel ? 'column' : 'window') : undefined
    const shouldUseTransformLayer = !isMobile && !onePanel && !panelFrame
    const bottomEditorHostScale =
        shouldUseTransformLayer && !isPanelCenter(panel.panelId) ? SMALL : BIG
    const naturalThreadLayout = naturalThreadLayoutEnabled(panel.filter)
    const isThreadPanel = Boolean(panel.filter?.questId)
    // fixed_header: the place bar belongs to the panel in focus (the only one on
    // phones / one-panel mode, the centre one in the desktop row). Side panels
    // are scaled down around their centre, so a bar outside that scale could
    // not line up with them anyway; they keep the plain 46px spacer.
    // fixed_header windows: a window that has just appeared (an empty slot
    // filled) takes its place without transitions; it animates from then on.
    const framedWindow = panelFrame === 'window' && !panel.empty
    const [frameSettled, setFrameSettled] = useState(false)
    useLayoutEffect(() => {
        if (!framedWindow) {
            setFrameSettled(false)
            return
        }
        let second = 0
        const first = requestAnimationFrame(() => {
            second = requestAnimationFrame(() => setFrameSettled(true))
        })
        return () => {
            cancelAnimationFrame(first)
            cancelAnimationFrame(second)
        }
    }, [framedWindow])
    // What the frame holds: a channel/list, a thread, or a new thread's editor.
    const panelKind: 'stream' | 'thread' | 'new-thread' | undefined = panelFrame
        ? panel.newThread
            ? 'new-thread'
            : isThreadPanel
            ? 'thread'
            : 'stream'
        : undefined
    // The panel in the middle of the row (in one-panel mode, the one on screen).
    const isCentrePanel = useStore(state => state.centerIndex === index)
    const isFocusedPanel = useStore(
        state => isMobile || state.numPanels === 1 || state.centerIndex === index,
    )
    // Phones and the one-panel column show the bar on the focused panel; in
    // the three-panel row every window carries its own header.
    const showPlaceBar =
        fixedHeaderEnabled() &&
        !panel.empty &&
        !!panel.filter &&
        (isFocusedPanel || panelFrame === 'window')
    // bottom_editor_dock: this thread's phone pill is placed by the page's dock.
    const bottomEditorDock =
        dock && bottomEditorMobileSurface ? getBottomEditorDock({ isAndroid }) : null
    const docked = Boolean(bottomEditorDock) && isThreadPanel && bottomEditorMounted
    useLayoutEffect(() => {
        if (!bottomEditorDock || !docked) return
        const panelElement = panelContainerRef.current
        const frameElement = bottomEditorHostRef.current
        if (!panelElement || !frameElement || !bottomEditorHost) return
        return bottomEditorDock.register(panel.panelId, {
            panelElement,
            host: frameElement,
            glide: bottomEditorHost,
        })
    }, [bottomEditorDock, docked, bottomEditorHost, panel.panelId])
    // A keyboard over a docked thread covers its end, and the reserve below the
    // last post keeps the lift until the keyboard has gone: for exactly that
    // long the thread reads as away from its bottom. The jump chip waits it
    // out — while the keyboard is present, and after it leaves until the
    // thread's end is back in view (the sentinel's next report) or, for a
    // reader who really is away, a moment later. Derived during render, so no
    // frame shows the chip between the keyboard leaving and the wait starting.
    // Every panel subscribes, so each reads only this flag: the dock's other
    // changes, a dismissal's start among them, land mid-slide and re-render no
    // panel.
    const dockSnapshotKeyboardPresent = useSyncExternalStore(
        bottomEditorDock ? bottomEditorDock.subscribe : subscribeToNoDock,
        () => keyboardEdgeActive() && Boolean(bottomEditorDock?.getSnapshot().keyboardPresent),
    )
    const dockKeyboardPresent = docked && dockSnapshotKeyboardPresent
    const [previousDockKeyboardPresent, setPreviousDockKeyboardPresent] =
        useState(dockKeyboardPresent)
    const [chipSettlingAfterKeyboard, setChipSettlingAfterKeyboard] = useState(false)
    if (previousDockKeyboardPresent !== dockKeyboardPresent) {
        setPreviousDockKeyboardPresent(dockKeyboardPresent)
        setChipSettlingAfterKeyboard(!dockKeyboardPresent)
    }
    useEffect(() => {
        if (!chipSettlingAfterKeyboard) return
        const timer = window.setTimeout(
            () => setChipSettlingAfterKeyboard(false),
            CHIP_KEYBOARD_SETTLE_MS,
        )
        return () => window.clearTimeout(timer)
    }, [chipSettlingAfterKeyboard])
    // The chip's own visibility, shared by the render and by a raise deciding
    // whether there is a chip on screen for the pill to grow out of.
    const chipWouldShow =
        isThreadPanel &&
        !threadAtBottom &&
        !externalHideBottomChip &&
        !dockKeyboardPresent &&
        !chipSettlingAfterKeyboard
    chipWouldShowRef.current = chipWouldShow
    const offerLabel = answerJumpOffer?.kind === 'open' ? 'View reply' : 'Your reply'
    const isNotificationPanel = Boolean(panel.filter?.notifications)
    const isOrdinalsPanel = Boolean(panel.filter?.ordinals || panel.filter?.user_ordinals)
    // Music's Discover and Artists tabs list songs and artists, not the Radio
    // feed, so the feed's "N New" chip has nothing there to point at.
    const musicView = useMusicPanelView(panel.panelId)
    const isMusicListTab =
        isRadioStreamFilter(panel.filter) && !panel.filter?.search && musicView.tab !== 'radio'
    const hasNotificationNewItems = Boolean(panel.filter?.notifications && numNoti > 0)
    const ordinalUpdatesCount = useOrdinalStreamUpdates(panel)
    const hasOrdinalUpdates = isOrdinalsPanel && ordinalUpdatesCount > 0

    const placementQuestId = String(panel.filter?.questId || '')
    const placementAnswerId = String(panel.filter?.answerId || '')
    const placementRouteKey = `${panel.panelId}:${placementQuestId}:${placementAnswerId}`
    const pendingPlacementMatchesRoute = Boolean(
        naturalThreadLayout &&
            placementQuestId &&
            placementAnswerId &&
            String(panel.pendingOpenPlacement?.questId || '') === placementQuestId &&
            String(panel.pendingOpenPlacement?.answerId || '') === placementAnswerId,
    )
    const pendingPlacementIntent = pendingPlacementMatchesRoute
        ? panel.pendingOpenPlacement
        : undefined
    const [openPlacementPaintGate, setOpenPlacementPaintGate] = useState<OpenPlacementPaintGate>(
        () => ({
            routeKey: placementRouteKey,
            questId: pendingPlacementMatchesRoute ? placementQuestId : null,
            answerId: pendingPlacementMatchesRoute ? placementAnswerId : null,
            intent: pendingPlacementIntent,
            revealed: !pendingPlacementMatchesRoute,
        }),
    )

    // A PanelContainer can be reused when an empty slot becomes a thread. Derive the new
    // route's gate during render so its first commit is hidden; an effect would be one paint
    // too late. Once an intent is consumed, retain the closed gate until the placement owner
    // explicitly releases it — pendingOpenPlacement itself is deliberately single-shot.
    let effectiveOpenPlacementPaintGate = openPlacementPaintGate
    if (
        openPlacementPaintGate.routeKey !== placementRouteKey ||
        (pendingPlacementIntent && openPlacementPaintGate.intent !== pendingPlacementIntent)
    ) {
        effectiveOpenPlacementPaintGate = {
            routeKey: placementRouteKey,
            questId: pendingPlacementMatchesRoute ? placementQuestId : null,
            answerId: pendingPlacementMatchesRoute ? placementAnswerId : null,
            intent: pendingPlacementIntent,
            revealed: !pendingPlacementMatchesRoute,
        }
        setOpenPlacementPaintGate(effectiveOpenPlacementPaintGate)
    }
    const hideForPendingOpenPlacement = Boolean(
        effectiveOpenPlacementPaintGate.questId &&
            effectiveOpenPlacementPaintGate.answerId &&
            !effectiveOpenPlacementPaintGate.revealed,
    )
    const revealPendingOpenPlacement = useCallback(
        (
            questId: string | number,
            answerId: string | number,
            intent?: PanelState['pendingOpenPlacement'],
        ) => {
            const normalizedQuestId = String(questId || '')
            const normalizedAnswerId = String(answerId || '')
            setOpenPlacementPaintGate(current => {
                if (
                    current.routeKey !== placementRouteKey ||
                    current.questId !== normalizedQuestId ||
                    current.answerId !== normalizedAnswerId ||
                    (intent && current.intent !== intent) ||
                    current.revealed
                ) {
                    return current
                }
                return { ...current, revealed: true }
            })
        },
        [placementRouteKey],
    )
    const failPendingOpenPlacement = useCallback(
        (
            questId: string | number,
            answerId: string | number,
            intent: PanelState['pendingOpenPlacement'],
            reason: OpenPlacementPaintGate['failure'] = 'timeout',
        ) => {
            if (!intent?.contextScroll || panel.pendingOpenPlacement !== intent) return
            if (
                String(panel.filter?.questId) !== String(questId) ||
                String(panel.filter?.answerId) !== String(answerId)
            )
                return
            panel.cancelPendingAnswerScroll()
            // Both the scroller deadline and the panel timer come through here. A
            // prepared post must not become an error just because context is late.
            const scrollElement = scrollContainerRef.current
            const anchor =
                reason !== 'unavailable' && scrollElement
                    ? installThreadOpenAnchor(scrollElement, String(answerId))
                    : null
            if (anchor) {
                fallbackOpenAnchorRef.current?.()
                fallbackOpenAnchorRef.current = anchor.release
                anchor.place(0)
                panel.consumeOpenPlacement(String(questId), String(answerId))
                revealPendingOpenPlacement(questId, answerId, intent)
                panel.highlightAnswer?.(String(answerId))
                return
            }
            setOpenPlacementPaintGate(current => {
                if (current.intent !== intent || current.revealed || current.failure) return current
                return { ...current, failure: reason }
            })
        },
        [panel, revealPendingOpenPlacement],
    )
    const retryPendingOpenPlacement = useCallback(() => {
        const current = effectiveOpenPlacementPaintGate
        if (!current.failure || !current.intent || panel.pendingOpenPlacement !== current.intent)
            return
        const { questId, answerId } = current.intent
        panel.cancelAnswerReveal(questId, answerId)
        const intent = { ...current.intent }
        panel.pendingOpenPlacement = intent
        setOpenPlacementPaintGate({ ...current, intent, failure: undefined })
        void prepareOpenRight(questId, answerId).catch(() => null)
        void panel.requestAnswerReveal(answerId, { questId, contextScroll: true, highlight: true })
    }, [effectiveOpenPlacementPaintGate, panel])
    useEffect(
        () => () => {
            fallbackOpenAnchorRef.current?.()
            fallbackOpenAnchorRef.current = null
        },
        [placementRouteKey],
    )
    const threadNewReplies = useThreadNewReplies(
        isThreadPanel ? panel.filter?.questId : null,
        threadAtBottom,
    )
    const lastAction = useStore(state => state.panels.lastAction)
    const dbg = useCallback((...args: unknown[]) => {
        try {
            if (DEBUG()) {
                // eslint-disable-next-line no-console
                console.log('[PanelContainer]', ...args)
            }
        } catch {}
    }, [])
    const shouldLiftBottomControls =
        !isMobile && numPanels === 3 && !showSidebar && index === getCenterIndex()
    // A window gliding off the row in a three-panel insert keeps its content painted
    // until the glide ends (lib/panelRowShift); slot reuse can't carry it over.
    const rowShiftKeepsContent = useStore(state =>
        Boolean(state.panels.rowShift?.boxes[panel.panelId]),
    )
    const panelContentActiveTarget =
        (rowShiftKeepsContent && hide !== true) ||
        shouldKeepPanelContentActive({
            activeIndex: panelActiveIndex,
            hidden: hide === true,
            numPanels,
            panelIndex: index,
            prewarmPanelIndex: onePanelPrewarmIndex,
        })
    const [retainOutgoingPanelContent, setRetainOutgoingPanelContent] =
        useState(panelContentActiveTarget)
    // Activation is synchronous with panel state so incoming content is ready before paint.
    // Deactivation is delayed below so the outgoing slide remains painted through transition.
    const panelContentActive = panelContentActiveTarget || retainOutgoingPanelContent
    const desktopPanelParkingEnabled = !isMobile && (numPanels === 1 || numPanels === 3)
    const desktopPanelParked = desktopPanelParkingEnabled && !panelContentActive
    const panelContentPrewarming = !isMobile && numPanels === 1 && index === onePanelPrewarmIndex
    const hidePanelPrewarm =
        panelContentPrewarming && index !== panelActiveIndex && !onePanelPrewarmVisible
    const effectiveHide = hide === true || desktopPanelParked
    const { newItemsCount, newItemsCapped, hasNewItems, newItemsAuthors } = useFeedNewItems(panel, {
        active: !effectiveHide && !panelContentPrewarming,
        scrollContainerRef,
    })

    useEffect(() => {
        if (!hideForPendingOpenPlacement || effectiveOpenPlacementPaintGate.failure) return
        const { questId, answerId } = effectiveOpenPlacementPaintGate
        if (!questId || !answerId) return

        // The deadline ends loading, not the user's target. A loaded target can be
        // placed directly; otherwise keep the content hidden and offer a retry.
        const pendingIntent = effectiveOpenPlacementPaintGate.intent
        const timeout = window.setTimeout(() => {
            if (panel.pendingOpenPlacement !== pendingIntent) return
            if (pendingIntent?.contextScroll) {
                failPendingOpenPlacement(questId, answerId, pendingIntent)
                return
            }
            panel.cancelPendingAnswerScroll?.()
            panel.consumeOpenPlacement?.(questId, answerId)
            markPanelNavMetric('open-placement-fallback', {
                reason: 'paint-gate-timeout',
                panelId: panel.panelId,
                questId,
                answerId,
            })
            revealPendingOpenPlacement(questId, answerId, pendingIntent)
        }, OPEN_PLACEMENT_PAINT_GATE_TIMEOUT_MS)

        // The coordinator may still be waiting for the first target descriptor,
        // before it can arm its scroller's cancellation listeners.
        const cancelOnInput = () => {
            if (panel.pendingOpenPlacement !== pendingIntent) return
            window.clearTimeout(timeout)
            panel.cancelPendingAnswerScroll()
            panel.consumeOpenPlacement(questId, answerId)
            revealPendingOpenPlacement(questId, answerId, pendingIntent)
        }
        const inputSurface = pendingIntent?.contextScroll ? panelContainerRef.current : null
        const events = ['wheel', 'touchstart', 'pointerdown']
        const cancelOnKeyDown = (event: KeyboardEvent) => {
            if (OPEN_PLACEMENT_CANCEL_KEYS.has(event.key)) cancelOnInput()
        }
        events.forEach(event =>
            inputSurface?.addEventListener(event, cancelOnInput, { passive: true }),
        )
        window.addEventListener('keydown', cancelOnKeyDown)
        return () => {
            window.clearTimeout(timeout)
            events.forEach(event => inputSurface?.removeEventListener(event, cancelOnInput))
            window.removeEventListener('keydown', cancelOnKeyDown)
        }
    }, [
        effectiveOpenPlacementPaintGate,
        hideForPendingOpenPlacement,
        panel,
        revealPendingOpenPlacement,
        failPendingOpenPlacement,
    ])

    const setScrollContainerRef = useCallback(
        (ref: HTMLDivElement | null) => {
            const previousScrollContainer = scrollContainerRef.current
            if (previousScrollContainer && previousScrollContainer !== ref) {
                panel.savedScrollTop = previousScrollContainer.scrollTop
            }
            scrollContainerRef.current = ref
        },
        [panel],
    )

    useEffect(() => {
        if (panelContentActiveTarget) {
            setRetainOutgoingPanelContent(true)
            return
        }

        // Swiper changes activeIndex before its horizontal transition finishes. Keep the
        // outgoing panel painted until it is offscreen, then park its content.
        const timeout = window.setTimeout(() => {
            // Capture while this is still a live scrolling box. The state update below
            // applies display:none on the next commit, after the offset is durable.
            if (desktopPanelParkingEnabled) {
                const scrollContainer = scrollContainerRef.current
                if (scrollContainer) {
                    panel.savedScrollTop = scrollContainer.scrollTop
                    retainDecodedPanelMedia(scrollContainer)
                }
            }
            setRetainOutgoingPanelContent(false)
        }, PANEL_CONTENT_DEACTIVATION_DELAY_MS)
        return () => window.clearTimeout(timeout)
    }, [desktopPanelParkingEnabled, panel, panelContentActiveTarget])

    // Pull-to-refresh state
    // Keep activity and source in one state transition. Under the
    // Gesture refreshes render the pull indicator while nav/search refreshes
    // light the SearchHeader halo instead.
    const [refreshState, setRefreshState] = useState(idleRefreshState)
    const [isInitialLoading, setIsInitialLoading] = useState(false)
    const { isRefreshing, source: refreshSource } = refreshState

    const fakeRefresh = useCallback(() => {
        setTimeout(() => {
            setRefreshState(idleRefreshState())
            logEv('PULL_TO_REFRESH', { filter: panel.filter, isFake: true })
        }, 500)
    }, [panel.filter])

    // Function to refresh content
    const refreshContent = useCallback(
        async (source: RefreshSource = 'nav') => {
            console.log('PanelContainer refreshContent called, filter:', panel.filter)
            if (!panel.filter && !panel.empty) return

            setRefreshState(activeRefreshState(source))

            // Handle empty panels or new quests differently
            if (panel.empty) {
                // For empty panels, just animate away after a short timeout without making a request
                fakeRefresh()
                return
            }

            // For non-empty panels, continue with normal refresh logic
            const newFilter = withDepth(panel.filter)
            const filterKey = serializeFilterKey(ordinalsStreamQueryFilter(newFilter))

            const notificationSpaceId = newFilter?.notifications
                ? useStore.getState().activeSpaceId
                : null
            const notificationBadgeEventVersion =
                notificationSpaceId != null
                    ? getSpaceNotificationBadgeEventVersion(notificationSpaceId)
                    : null

            // For quest containers (single quest)
            if (panel.filter?.questId) {
                const quest = getCachedQuest(panel.filter.questId)
                if (quest?.is_draft || quest?.isLocalChildQuest || quest?.isLocalRootQuest) {
                    fakeRefresh()
                    return
                } else {
                    await queryClient.invalidateQueries({
                        queryKey: ['quest', panel.filter.questId],
                    })
                }
            } else if (panel.filter?.user_music) {
                await queryClient.invalidateQueries({
                    queryKey: ['radioArtist', panel.filter.user_music],
                })
            } else if (panel.filter?.history) {
                // For history panels
                await queryClient.invalidateQueries({ queryKey: ['actions'] })
            } else if (panel.filter?.streams) {
                // For streams panels - let the Streams component handle the refresh state
                await queryClient.invalidateQueries({ queryKey: ['streams'] })
                logEv('PULL_TO_REFRESH', { filter: panel.filter })
                return // Don't reset isRefreshing here
            } else if (panel.filter?.memes) {
                // For memes panels - let the Memes component handle the refresh state
                await queryClient.invalidateQueries({ queryKey: ['memes'] })
                logEv('PULL_TO_REFRESH', { filter: panel.filter })
                return // Don't reset isRefreshing here
            } else if (panel.filter?.ordinals || panel.filter?.user_ordinals) {
                await queryClient.invalidateQueries({
                    queryKey: ['ordinalsMediaStream', filterKey],
                    exact: true,
                })
            } else {
                const questQueryKey = getQuestsQueryKey(
                    newFilter,
                    newFilter?.notifications ? { clear: true } : {},
                )
                if (naturalFeedLayoutEnabled(newFilter)) {
                    resetNaturalFeedSessionSnapshot(String(questQueryKey[1]))
                }
                const refetch = () =>
                    queryClient.invalidateQueries({ queryKey: questQueryKey, exact: true })
                if (isRadioStreamFilter(newFilter)) {
                    // Music's Artists and Discover tabs rank from the same ledgers: refresh them too.
                    void queryClient.invalidateQueries({ queryKey: ['radioArtists'] })
                    void queryClient.invalidateQueries({ queryKey: ['musicDiscover'] })
                    void queryClient.invalidateQueries({ queryKey: ['myMusic'] })
                    void queryClient.invalidateQueries({ queryKey: ['radioArtist'] })
                }
                await (isRadioStreamFilter(newFilter)
                    ? refreshRadioStreamQuery(queryClient, questQueryKey, refetch)
                    : refetch())
                if (notificationSpaceId != null && notificationBadgeEventVersion != null) {
                    useStore
                        .getState()
                        .set(state =>
                            consumeSpaceNotificationBadgeIfVersionMatches(
                                state,
                                notificationSpaceId,
                                notificationBadgeEventVersion,
                            ),
                        )
                }
            }

            setRefreshState(idleRefreshState())
            logEv('PULL_TO_REFRESH', { filter: panel.filter })
        },
        [panel, panel.filter],
    )

    // Gesture components call onRefresh() with no args; nav-driven refreshes
    // (PanelState.triggerStreamRefresh) come through the registered handler.
    const refreshFromGesture = useCallback(() => refreshContent('gesture'), [refreshContent])
    const refreshFromNav = useCallback(() => refreshContent('nav'), [refreshContent])

    // SearchHeader/Search/Streams/Memes drive setIsRefreshing directly through
    // panel context; tag those as 'search' so they never render pull chrome.
    const setIsRefreshingFromHeader = useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
        value => {
            setRefreshState(current => updateHeaderRefreshState(current, value))
        },
        [],
    )

    useEffect(() => {
        panel.registerRefreshHandler(refreshFromNav)
        return () => panel.registerRefreshHandler(null)
    }, [panel, refreshFromNav])

    // The pull indicator only answers for real pulls; other sources render as
    // the SearchHeader halo.
    const refreshPresentation = resolveRefreshPresentation({
        isRefreshing,
        source: refreshSource,
    })
    const gestureIsRefreshing = refreshPresentation.showPullIndicator

    // Source-specific chrome either scrolls away or sits at the top edge, while
    // the capsule is the refresh-owning control pinned at every scroll depth.
    const capsuleCarriesRefreshHalo = refreshPresentation.showCapsuleHalo

    const updateFloatingNavVisibility = useCallback(() => {
        if (isMobile) return

        const left = floatingHeaderNavLeftRef.current
        const right = floatingHeaderNavRightRef.current
        const sc = scrollContainerRef.current
        const isCenter = isPanelCenter(panel.panelId)
        if (!left || !right) return
        if (!sc || !isCenter) {
            left.classList.remove(styles.isVisible)
            right.classList.remove(styles.isVisible)
            return
        }
        // Compute gating live
        let prevOk = false
        let nextOk = false
        const num = useStore.getState().numPanels
        const swiper = getSwiperRef()?.current
        if (num === 1) {
            prevOk = Boolean(swiper && !swiper.isBeginning)
            nextOk = Boolean(swiper && !swiper.isEnd)
        } else {
            const panelsState = useStore.getState().panels.state
            const centerIx = getCenterIndex()
            const l = panelsState[centerIx - 1]
            const r = panelsState[centerIx + 1]
            prevOk = false //Boolean(l && !l.empty)
            nextOk = false //Boolean(r && !r.empty)
        }
        left.classList.toggle(styles.isVisible, prevOk)
        right.classList.toggle(styles.isVisible, nextOk)
    }, [panel.panelId])

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current
        const scrollTopSentinel = scrollTopSentinelRef.current
        if (!scrollContainer || !scrollTopSentinel || typeof IntersectionObserver === 'undefined') {
            return
        }

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries.find(candidate => candidate.target === scrollTopSentinel)
                if (!entry) return

                const awayFromTop = !entry.isIntersecting
                floatingFilterRef.current?.classList.toggle(styles.visible, awayFromTop)
                removeButtonContainerRef.current?.classList.toggle(styles.isVisible, awayFromTop)
            },
            { root: scrollContainer, threshold: 0 },
        )
        observer.observe(scrollTopSentinel)
        return () => observer.disconnect()
    }, [panel.panelId])

    // Feed pill: keep the capsule visible while it has news, regardless of
    // scroll position. Imperative class (same discipline as the observer's
    // `visible` toggle above) because this node's className prop must stay
    // static — a React-managed class here would clobber the observer's.
    useEffect(() => {
        floatingFilterRef.current?.classList.toggle(
            styles.hasNewItems,
            hasNewItems || hasNotificationNewItems || hasOrdinalUpdates,
        )
    }, [hasNewItems, hasNotificationNewItems, hasOrdinalUpdates])

    // Thread chip: away-from-bottom gating via a sentinel at the end of the
    // in-flow thread content. State updates fire on boundary transitions only.
    // The end counts from a little short of it (THREAD_END_SLACK_PX): a
    // reader a thumb's flick from the last reply is still reading the end.
    useEffect(() => {
        if (!isThreadPanel) return
        const scrollContainer = scrollContainerRef.current
        const sentinel = scrollBottomSentinelRef.current
        if (!scrollContainer || !sentinel || typeof IntersectionObserver === 'undefined') {
            return
        }

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries.find(candidate => candidate.target === sentinel)
                if (!entry) return
                setThreadAtBottom(entry.isIntersecting)
                if (entry.isIntersecting) setChipSettlingAfterKeyboard(false)
                endVisibleRef.current = entry.isIntersecting
                const scrollTop = scrollContainer.scrollTop
                readingEndRef.current = resolveReadingThreadEnd({
                    endVisible: entry.isIntersecting,
                    scrollTop,
                    endSeenScrollTop: endSeenScrollTopRef.current,
                    slackPx: THREAD_END_SLACK_PX,
                })
                if (entry.isIntersecting) endSeenScrollTopRef.current = scrollTop
            },
            {
                root: scrollContainer,
                rootMargin: `0px 0px ${THREAD_END_SLACK_PX}px 0px`,
                threshold: 0,
            },
        )
        observer.observe(sentinel)
        return () => {
            observer.disconnect()
            setThreadAtBottom(true)
        }
    }, [isThreadPanel, panel.panelId])

    // The reader scrolling by hand (a wheel, a drag, the scrollbar) with the
    // thread's end out of view has left it; another thread starts over.
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current
        readingEndRef.current = endVisibleRef.current
        endSeenScrollTopRef.current =
            endVisibleRef.current && scrollContainer ? scrollContainer.scrollTop : null
        if (!isThreadPanel || !scrollContainer) return
        const stopFollowing = () => {
            if (!endVisibleRef.current) readingEndRef.current = false
        }
        const stopOnScrollbar = (event: PointerEvent) => {
            if (event.target === scrollContainer) stopFollowing()
        }
        scrollContainer.addEventListener('wheel', stopFollowing, { passive: true })
        scrollContainer.addEventListener('touchmove', stopFollowing, { passive: true })
        scrollContainer.addEventListener('pointerdown', stopOnScrollbar)
        return () => {
            scrollContainer.removeEventListener('wheel', stopFollowing)
            scrollContainer.removeEventListener('touchmove', stopFollowing)
            scrollContainer.removeEventListener('pointerdown', stopOnScrollbar)
        }
    }, [isThreadPanel, panel.panelId, currentQuestId])

    useLayoutEffect(() => {
        if (!isThreadPanel) return

        const scrollContainer = scrollContainerRef.current
        const transformContainer = transformContainerRef.current
        const contentBottomSentinel = scrollBottomSentinelRef.current
        const gutter = threadBottomGutterRef.current
        if (!scrollContainer || !transformContainer || !contentBottomSentinel || !gutter) return

        let frame = 0
        let observedAnchor: HTMLElement | null = null
        let observedBottomEditorPill: HTMLElement | null = null
        // The rest of the pill (all of it but its field), from its last open
        // measure at rest (canMeasureBottomEditorField), in viewport px; and
        // the composer's fold motions, each seen to its end once.
        let bottomEditorFieldRest: number | null = null
        let bottomEditorFieldPill: HTMLElement | null = null
        const watchedBottomEditorResizes = new WeakSet<Animation>()
        let disposed = false
        // The reserve is being held above what the pill needs (see below).
        let retainingReserve = false
        const resolveEndAnchor = () => {
            const semanticAnchors = transformContainer.querySelectorAll<HTMLElement>(
                PANEL_SCROLL_END_ANCHOR_SELECTOR,
            )
            if (semanticAnchors.length > 0) return semanticAnchors[semanticAnchors.length - 1]

            const answerRows =
                transformContainer.querySelectorAll<HTMLElement>(PANEL_SCROLL_ROW_SELECTOR)
            return answerRows.length > 0 ? answerRows[answerRows.length - 1] : null
        }

        const resolveLayoutScale = () => {
            const transformRect = transformContainer.getBoundingClientRect()
            const scale =
                transformContainer.offsetHeight > 0
                    ? transformRect.height / transformContainer.offsetHeight
                    : 1
            return Number.isFinite(scale) && scale > 0 ? scale : 1
        }

        // bottom_editor: the pill's measured clearance from the scrollport's
        // bottom edge sizes the reserve at the end of the thread and lifts the
        // jump-to-latest chip and the close control above the pill. The pill
        // lives in the panel's host, outside the transform container, so it is
        // looked up and observed separately from the flow content.
        // The pill's foot moved (the viewport for the keyboard, the spacer
        // for the navbar): hold it on screen and glide it to its new place
        // (bottomEditorGlide). Only the phone's foot in a keyboard motion.
        const glideBottomEditor = (translateY: number) => {
            // Under the dock the host's place is the dock's, applied directly.
            if (docked) return
            const frameElement = bottomEditorHostRef.current
            if (!bottomEditorHost || !frameElement || !bottomEditorMobileSurface) return
            const frameRect = frameElement.getBoundingClientRect()
            const step = resolveGlide({
                state: bottomEditorGlideStateRef.current,
                frame: {
                    hostBottom: frameRect.bottom,
                    spacer: parseFloat(getComputedStyle(frameElement).paddingBottom) || 0,
                },
                translateY,
                phase: keyboardPhase,
                rememberedKeyboardHeight,
            })
            bottomEditorGlideStateRef.current = step.state
            applyGlideStep(bottomEditorHost, step, {
                reduceMotion: Boolean(
                    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches,
                ),
            })
        }

        const readGlideTranslateY = () =>
            bottomEditorHost
                ? readTransformTranslateY(getComputedStyle(bottomEditorHost).transform) || 0
                : 0
        // The chrome slide is on the outer host (the frame), not the glide
        // inside it that wears the keyboard's transform. Read from the glide,
        // it was always 0: hiding the navbar remeasured the pill 35px lower,
        // and the chip dropped onto the pill while the reserve and the reader
        // at the end jumped by the same.
        const readChromeSlideY = () => {
            const frameElement = bottomEditorHostRef.current
            return frameElement
                ? readTranslatePropertyY(getComputedStyle(frameElement).translate)
                : 0
        }
        // A long draft stops the pill below the panel's top controls, and its
        // field scrolls from there (resolveBottomEditorFieldMaxHeight): the
        // room from the pill's resting foot (any glide taken out) up to the
        // floating filter, less the rest of the pill. Read here; the caller
        // writes it once its own reads are done.
        const measureBottomEditorFieldCap = (
            pill: HTMLElement,
            pillRect: DOMRect,
            scrollRect: DOMRect,
            glideTranslateY: number,
        ): string | null => {
            if (pill !== bottomEditorFieldPill) {
                bottomEditorFieldPill = pill
                bottomEditorFieldRest = null
            }
            const composer = pill.querySelector<HTMLElement>('[data-inline-composer]')
            const field = pill.querySelector<HTMLElement>('[data-inline-editor-container="true"]')
            if (!field) return null
            const resizes = (composer?.getAnimations?.() ?? []).filter(isBottomEditorResize)
            resizes.forEach(resize => {
                if (watchedBottomEditorResizes.has(resize)) return
                watchedBottomEditorResizes.add(resize)
                resize.finished.then(settleBottomEditorFieldCap, settleBottomEditorFieldCap)
            })
            if (
                canMeasureBottomEditorField({
                    folded: composer?.hasAttribute('data-inline-composer-folded') ?? false,
                    resizing: resizes.length > 0,
                })
            ) {
                bottomEditorFieldRest = pillRect.height - field.getBoundingClientRect().height
            }
            if (bottomEditorFieldRest == null) return null
            return `${resolveBottomEditorFieldMaxHeight({
                pillBottom: pillRect.bottom - glideTranslateY,
                rest: bottomEditorFieldRest,
                topLimit: scrollRect.top + getPanelScrollTopInset(scrollContainer),
                scale: resolveLayoutScale(),
            })}px`
        }
        const writeBottomEditorFieldCap = (panelElement: HTMLElement, cap: string | null) => {
            if (
                !cap ||
                panelElement.style.getPropertyValue(BOTTOM_EDITOR_FIELD_MAX_HEIGHT_VAR) === cap
            ) {
                return
            }
            panelElement.style.setProperty(BOTTOM_EDITOR_FIELD_MAX_HEIGHT_VAR, cap)
        }
        // At once, between the panel's frames: an unfold measures the size its
        // motion aims at as soon as it renders open (NewAnswer, through the
        // panel context), and a fold's motion is measured again as it ends,
        // before its last frame is drawn.
        const settleBottomEditorFieldCap = () => {
            if (disposed) return
            const panelElement = panelContainerRef.current
            const pill = panelElement?.querySelector<HTMLElement>(
                '[data-bottom-editor-pill="true"]',
            )
            if (!panelElement || !pill) return
            writeBottomEditorFieldCap(
                panelElement,
                measureBottomEditorFieldCap(
                    pill,
                    pill.getBoundingClientRect(),
                    scrollContainer.getBoundingClientRect(),
                    readGlideTranslateY() + readChromeSlideY(),
                ),
            )
        }
        measureBottomEditorFieldRef.current = settleBottomEditorFieldCap

        const syncBottomEditorClearance = () => {
            const panelElement = panelContainerRef.current
            if (!panelElement) return
            const pill = panelElement.querySelector<HTMLElement>('[data-bottom-editor-pill="true"]')
            observeCurrentBottomEditorPill(pill)
            if (!pill) {
                lastBottomEditorClearanceRef.current = null
                panelElement.style.removeProperty('--bottom-editor-chip-bottom')
                panelElement.style.removeProperty('--bottom-editor-reserve-clearance')
                panelElement.style.removeProperty(BOTTOM_EDITOR_FIELD_MAX_HEIGHT_VAR)
                bottomEditorFieldPill = null
                bottomEditorFieldRest = null
                return
            }
            // The pill's layout place, with any glide it is mid-way through
            // taken back out: the reserve below the card and the reader's
            // scroll follow where the pill is going, not where it is passing.
            const glideTranslateY = readGlideTranslateY()
            // The phone chrome's scroll slide (a separate `translate`, see
            // panel-container.module.scss) is taken out too: the chip and the
            // reserve are placed at rest and slide with the pill on their own.
            const restOffsetY = glideTranslateY + readChromeSlideY()
            const pillRect = pill.getBoundingClientRect()
            const scrollRect = scrollContainer.getBoundingClientRect()
            const clearance = Math.max(
                0,
                Math.round(scrollRect.bottom - (pillRect.top - restOffsetY)),
            )
            const fieldCap = measureBottomEditorFieldCap(pill, pillRect, scrollRect, restOffsetY)
            glideBottomEditor(glideTranslateY)
            writeBottomEditorFieldCap(panelElement, fieldCap)
            // The fixed chip consumes viewport pixels, while the reserve and
            // scrollTop live inside the panel's scaled layout coordinate space.
            // Keep both values so an 85%-scale side panel does not scale the
            // already-physical clearance a second time.
            const layoutClearance = clearance / resolveLayoutScale()
            const lastClearance = lastBottomEditorClearanceRef.current
            const previous =
                lastClearance?.panelId === panel.panelId ? lastClearance.layoutClearance : null
            // Under the dock the thread never scrolls for the pill: the reserve
            // below the last post grows at once, and shrinks only by what lies
            // below the reader's view, so no shrink clamps the scroll position
            // (resolveRetainedClearance). The chip follows the pill as it is.
            const reserveClearance = docked
                ? resolveRetainedClearance({
                      required: layoutClearance,
                      current: previous,
                      distanceFromBottom:
                          scrollContainer.scrollHeight -
                          scrollContainer.scrollTop -
                          scrollContainer.clientHeight,
                  })
                : layoutClearance
            retainingReserve = reserveClearance > layoutClearance
            lastBottomEditorClearanceRef.current = {
                panelId: panel.panelId,
                layoutClearance: reserveClearance,
            }
            panelElement.style.setProperty('--bottom-editor-chip-bottom', `${clearance}px`)
            panelElement.style.setProperty(
                '--bottom-editor-reserve-clearance',
                `${reserveClearance}px`,
            )
            // The reserve grows with the pill (a longer draft, a quote, the
            // keyboard). A reader at the bottom stays there: scroll by the same
            // amount so the last reply holds its place instead of sliding under
            // the pill and flipping the thread to "away from bottom". The
            // viewport shrinking for the keyboard is not this: the thread stays
            // where it is and the keyboard and the pill overlay its end.
            if (
                !docked &&
                previous != null &&
                layoutClearance !== previous &&
                threadAtBottomRef.current
            ) {
                scrollContainer.scrollTop += layoutClearance - previous
            }
        }

        const updateGutter = () => {
            frame = 0
            syncBottomEditorClearance()
            observeCurrentReserve()
            // Under the dock the gutter keeps its height through the keyboard:
            // collapsing it removed scroll range from under the reader.
            if (keyboardOpen && !docked) {
                gutter.style.height = '0px'
                return
            }

            const endAnchor = resolveEndAnchor()
            observeCurrentAnchor(endAnchor)
            if (!endAnchor) {
                gutter.style.height = '0px'
                return
            }

            const transformRect = transformContainer.getBoundingClientRect()
            const contentBottomRect = contentBottomSentinel.getBoundingClientRect()
            const endAnchorRect = endAnchor.getBoundingClientRect()
            const layoutScale = resolveLayoutScale()
            const height = calculatePanelEndGutterHeight({
                // Sized by the resting scrollport, not the keyboard-shrunk one:
                // otherwise the viewport growing back at close shrinks the range
                // before the gutter regrows, and a reader at the end is clamped.
                clientHeight:
                    scrollContainer.clientHeight +
                    (docked ? bottomEditorDock?.currentShrink() || 0 : 0),
                topInset: getPanelScrollTopInset(scrollContainer) / layoutScale,
                contentBottom: (contentBottomRect.bottom - transformRect.top) / layoutScale,
                endAnchorTop: (endAnchorRect.top - transformRect.top) / layoutScale,
                bottomInset:
                    isMobile && isAndroid
                        ? getPanelScrollBottomInset(scrollContainer) / layoutScale
                        : 0,
            })

            gutter.style.height = `${height}px`
        }

        const scheduleUpdate = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(updateGutter)
            // The pill's host resized (a draft grew, the navbar measured):
            // the dock re-measures its resting place.
            if (docked) bottomEditorDock?.update()
        }
        // A held reserve is released as the reader scrolls away from it —
        // watched by intersection, like the rest of this panel's scroll-driven
        // chrome, so no scroll listener runs on the scroll hot path.
        const reserveObserver =
            docked && typeof IntersectionObserver !== 'undefined'
                ? new IntersectionObserver(
                      () => {
                          if (retainingReserve) scheduleUpdate()
                      },
                      { root: scrollContainer, threshold: RESERVE_RELEASE_THRESHOLDS },
                  )
                : null
        let observedReserve: Element | null = null
        const observeCurrentReserve = () => {
            if (!reserveObserver) return
            const reserve = transformContainer.querySelector('[data-bottom-editor-reserve="true"]')
            if (reserve === observedReserve) return
            if (observedReserve) reserveObserver.unobserve(observedReserve)
            observedReserve = reserve
            if (observedReserve) reserveObserver.observe(observedReserve)
        }

        const resizeObserver =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate)
        const observeCurrentAnchor = (anchor: HTMLElement | null) => {
            if (anchor === observedAnchor) return
            if (observedAnchor) resizeObserver?.unobserve(observedAnchor)
            observedAnchor = anchor
            if (observedAnchor) resizeObserver?.observe(observedAnchor)
        }
        const observeCurrentBottomEditorPill = (pill: HTMLElement | null) => {
            if (pill === observedBottomEditorPill) return
            if (observedBottomEditorPill) resizeObserver?.unobserve(observedBottomEditorPill)
            observedBottomEditorPill = pill
            if (observedBottomEditorPill) resizeObserver?.observe(observedBottomEditorPill)
        }

        resizeObserver?.observe(scrollContainer)
        if (floatingFilterRef.current) resizeObserver?.observe(floatingFilterRef.current)
        if (bottomEditorHostRef.current) {
            resizeObserver?.observe(bottomEditorHostRef.current, { box: 'border-box' })
        }
        if (isMobile && isAndroid) {
            const bottomObstruction = scrollContainer.ownerDocument.querySelector<HTMLElement>(
                PANEL_SCROLL_BOTTOM_OBSTRUCTION_SELECTOR,
            )
            if (bottomObstruction) resizeObserver?.observe(bottomObstruction)
        }

        const mutationObserver =
            typeof MutationObserver === 'undefined' ? null : new MutationObserver(scheduleUpdate)
        mutationObserver?.observe(transformContainer, { childList: true, subtree: true })
        // Desktop applies the side-panel scale after this effect's first
        // measurement. Transforms do not resize the observed border box, so
        // watch the transform container's inline style and its completed CSS
        // transition explicitly to recompute layout-space clearance at 0.85.
        const transformMutationObserver =
            typeof MutationObserver === 'undefined' ? null : new MutationObserver(scheduleUpdate)
        transformMutationObserver?.observe(transformContainer, {
            attributes: true,
            attributeFilter: ['style'],
        })
        // The composer host sits outside the transform container; the pill
        // mounting there is what switches the measurement on.
        if (bottomEditorHostRef.current) {
            mutationObserver?.observe(bottomEditorHostRef.current, {
                childList: true,
                subtree: true,
            })
        }
        // Under the dock an overlay keyboard lifts the host through its inline
        // style (the dock writes only changes), from viewport samples this
        // effect does not all see — the visual viewport's scroll among them.
        // The reserve and the chip follow the pill to wherever it now stands.
        const dockShiftObserver =
            docked && bottomEditorHostRef.current && typeof MutationObserver !== 'undefined'
                ? new MutationObserver(scheduleUpdate)
                : null
        if (dockShiftObserver && bottomEditorHostRef.current) {
            dockShiftObserver.observe(bottomEditorHostRef.current, {
                attributes: true,
                attributeFilter: ['style'],
            })
        }
        bottomEditorHost?.addEventListener('transitionend', scheduleUpdate)
        transformContainer.addEventListener('transitionend', scheduleUpdate)
        window.addEventListener('resize', scheduleUpdate)
        window.visualViewport?.addEventListener('resize', scheduleUpdate)
        scheduleUpdate()

        return () => {
            disposed = true
            if (measureBottomEditorFieldRef.current === settleBottomEditorFieldCap) {
                measureBottomEditorFieldRef.current = null
            }
            cancelAnimationFrame(frame)
            resizeObserver?.disconnect()
            mutationObserver?.disconnect()
            transformMutationObserver?.disconnect()
            bottomEditorHost?.removeEventListener('transitionend', scheduleUpdate)
            transformContainer.removeEventListener('transitionend', scheduleUpdate)
            window.removeEventListener('resize', scheduleUpdate)
            window.visualViewport?.removeEventListener('resize', scheduleUpdate)
            reserveObserver?.disconnect()
            dockShiftObserver?.disconnect()
        }
    }, [
        bottomEditorHost,
        bottomEditorHostScale,
        bottomEditorInset,
        bottomNavigationVisible,
        isThreadPanel,
        keyboardHeight,
        keyboardOpen,
        keyboardPhase,
        rememberedKeyboardHeight,
        panel.panelId,
        docked,
        bottomEditorDock,
    ])

    const updateAndroidBottomChipOffset = useCallback(() => {
        const panelElement = panelContainerRef.current
        const bottomNavigation = document.querySelector<HTMLElement>('[data-bottom-navigation]')

        // The measured clearance is written to the panel, not to the chip: the
        // post-reply offer is the chip's sibling, and a custom property set on
        // the chip is invisible to it. Set there, the offer silently fell back
        // to the unmeasured 65px and could land under the navbar.
        if (!panelElement || !bottomNavigation || bottomNavigation.getClientRects().length === 0) {
            panelElement?.style.removeProperty('--android-bottom-chip-offset')
            return
        }

        const panelRect = panelElement.getBoundingClientRect()
        const navigationRect = bottomNavigation.getBoundingClientRect()
        const visualViewport = window.visualViewport
        const visibleViewportBottom = visualViewport
            ? visualViewport.offsetTop + visualViewport.height
            : window.innerHeight
        const offset = getBottomControlOffset({
            panelBottom: panelRect.bottom,
            navigationTop: resolveNavigationTop({
                // At rest: the controls may be slid away on scroll.
                rectTop: navigationRect.top - bottomControlsSlideY(bottomNavigation),
                transform: getComputedStyle(bottomNavigation).transform,
            }),
            visibleViewportBottom,
            gap: 10,
        })

        panelElement.style.setProperty('--android-bottom-chip-offset', `${offset}px`)
    }, [])

    useLayoutEffect(() => {
        if (!isMobile || !isAndroid) return

        let frame = 0
        const scheduleUpdate = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(updateAndroidBottomChipOffset)
        }
        const bottomNavigation = document.querySelector<HTMLElement>('[data-bottom-navigation]')
        const resizeObserver =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate)

        if (panelContainerRef.current) resizeObserver?.observe(panelContainerRef.current)
        if (bottomNavigation) resizeObserver?.observe(bottomNavigation)

        window.addEventListener('resize', scheduleUpdate)
        window.visualViewport?.addEventListener('resize', scheduleUpdate)
        scheduleUpdate()

        return () => {
            cancelAnimationFrame(frame)
            resizeObserver?.disconnect()
            window.removeEventListener('resize', scheduleUpdate)
            window.visualViewport?.removeEventListener('resize', scheduleUpdate)
        }
    }, [isThreadPanel, panel.panelId, showSidebar, updateAndroidBottomChipOffset])

    // bottom_editor on a phone: the pill stands on the navbar, so the host's
    // foot clears the navbar's measured height — safe-area padding, Android's
    // system-controls offset and all — rather than the bare device inset. The
    // clearance is written to the panel for the spacer to read (see
    // resolvePanelBottomSpacers); with the navbar away it is removed and the
    // inset takes over.
    const updateBottomEditorNavigationClearance = useCallback(() => {
        const panelElement = panelContainerRef.current
        if (!panelElement) return
        const bottomNavigation = document.querySelector<HTMLElement>('[data-bottom-navigation]')
        if (
            !bottomNavigationVisible ||
            !bottomNavigation ||
            bottomNavigation.getClientRects().length === 0
        ) {
            panelElement.style.removeProperty(BOTTOM_EDITOR_NAVIGATION_CLEARANCE_VAR)
            return
        }

        const panelRect = panelElement.getBoundingClientRect()
        const navigationRect = bottomNavigation.getBoundingClientRect()
        const visualViewport = window.visualViewport
        // Under the dock the resting clearance is the navbar's alone: an
        // overlay keyboard covering the foot must not remeasure it.
        const visibleViewportBottom = docked
            ? Number.POSITIVE_INFINITY
            : visualViewport
            ? visualViewport.offsetTop + visualViewport.height
            : window.innerHeight
        // Measured where the navbar stands, not where its return from the
        // keyboard has it in this same render (resolveNavigationTop), nor where
        // scrolling has slid it (a resize while it is away measured no navbar,
        // and the pill, its chip and the feed's foot all dropped and stayed).
        const clearance = getBottomControlOffset({
            panelBottom: panelRect.bottom,
            navigationTop: resolveNavigationTop({
                rectTop: navigationRect.top - bottomControlsSlideY(bottomNavigation),
                transform: getComputedStyle(bottomNavigation).transform,
            }),
            visibleViewportBottom,
            gap: 0,
        })
        panelElement.style.setProperty(BOTTOM_EDITOR_NAVIGATION_CLEARANCE_VAR, `${clearance}px`)
    }, [bottomNavigationVisible, docked])

    useLayoutEffect(() => {
        if (!bottomEditorMobileSurface || !isThreadPanel || !bottomEditorMounted) {
            panelContainerRef.current?.style.removeProperty(BOTTOM_EDITOR_NAVIGATION_CLEARANCE_VAR)
            return
        }

        let frame = 0
        const scheduleUpdate = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(updateBottomEditorNavigationClearance)
        }
        const bottomNavigation = document.querySelector<HTMLElement>('[data-bottom-navigation]')
        const resizeObserver =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate)

        if (panelContainerRef.current) resizeObserver?.observe(panelContainerRef.current)
        if (bottomNavigation) resizeObserver?.observe(bottomNavigation)

        window.addEventListener('resize', scheduleUpdate)
        window.visualViewport?.addEventListener('resize', scheduleUpdate)
        updateBottomEditorNavigationClearance()

        return () => {
            cancelAnimationFrame(frame)
            resizeObserver?.disconnect()
            window.removeEventListener('resize', scheduleUpdate)
            window.visualViewport?.removeEventListener('resize', scheduleUpdate)
        }
    }, [
        bottomEditorMobileSurface,
        isThreadPanel,
        bottomEditorMounted,
        updateBottomEditorNavigationClearance,
    ])

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current
        if (!scrollContainer || typeof IntersectionObserver === 'undefined') return

        let observer: IntersectionObserver | null = null
        let observedTarget: Element | null = null

        const refreshObserver = () => {
            const nextTarget = getActiveMediaPlaybackVisibilityElement({
                scopeElement: scrollContainer,
            }) as Element | null
            if (nextTarget === observedTarget) return

            observer?.disconnect()
            observer = null
            observedTarget = nextTarget
            if (!nextTarget) return

            observer = new IntersectionObserver(
                entries => {
                    const entry = entries.find(candidate => candidate.target === nextTarget)
                    if (!entry) return
                    dockActiveMediaPlaybackIfOutsideVisibleArea({
                        scopeElement: scrollContainer,
                        visibilityRoot: scrollContainer,
                    })
                },
                { root: null, threshold: 0 },
            )
            observer.observe(nextTarget)
        }

        const unsubscribe = subscribeToMediaDock(refreshObserver)
        refreshObserver()
        return () => {
            unsubscribe()
            observer?.disconnect()
        }
    }, [panel.panelId])

    // Compute slide gating for floating header controls in both modes
    useEffect(() => {
        if (isMobile) return
        if (!isPanelCenter(panel.panelId)) {
            setCanSlidePrev(false)
            setCanSlideNext(false)
            // Hide when not center
            const left = floatingHeaderNavLeftRef.current
            const right = floatingHeaderNavRightRef.current
            left?.classList.remove(styles.isVisible)
            right?.classList.remove(styles.isVisible)
            return
        }
        const num = useStore.getState().numPanels
        if (num === 1) {
            const swiper = getSwiperRef()?.current
            setCanSlidePrev(Boolean(swiper && !swiper.isBeginning))
            setCanSlideNext(Boolean(swiper && !swiper.isEnd))
        } else {
            false
        }
    }, [activeIndex, panelsLength, panel.panelId, onePanel])

    // Keep floating header controls visibility in sync on active and mode changes.
    useEffect(() => {
        if (isMobile) return
        const frame = requestAnimationFrame(updateFloatingNavVisibility)
        return () => cancelAnimationFrame(frame)
    }, [activeIndex, onePanel, panel.panelId, numPanels, updateFloatingNavVisibility])

    const slidePrev = useCallback(
        (event: React.MouseEvent) => {
            if (!canSlidePrev) return
            const swiper = getSwiperRef()?.current
            if (swiper) {
                const panels = useStore.getState().panels.state
                if (!hasPanelInDirection(panels, getCenterIndex(), 'prev')) {
                    playPanelBoundaryFeedback(swiper, 'prev')
                    return
                }

                startPanelNavMetric('header-arrow', {
                    activeIndex: swiper.activeIndex,
                    direction: 'prev',
                    isMobile,
                    onePanel,
                    ...getPanelNavInputDetail(event),
                })
                slideToPanel(getCenterIndex() - 1, SWIPE_SPEED_SLOW)
            }
        },
        [canSlidePrev, onePanel],
    )

    const slideNext = useCallback(
        (event: React.MouseEvent) => {
            if (!canSlideNext) return
            const swiper = getSwiperRef()?.current
            if (swiper) {
                const panels = useStore.getState().panels.state
                if (!hasPanelInDirection(panels, getCenterIndex(), 'next')) {
                    playPanelBoundaryFeedback(swiper, 'next')
                    return
                }

                startPanelNavMetric('header-arrow', {
                    activeIndex: swiper.activeIndex,
                    direction: 'next',
                    isMobile,
                    onePanel,
                    ...getPanelNavInputDetail(event),
                })
                slideToPanel(getCenterIndex() + 1, SWIPE_SPEED_SLOW)
            }
        },
        [canSlideNext, onePanel],
    )

    const handleScrollToTop = useCallback(() => {
        scrollContainerRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
        })
    }, [])

    const handleScrollToBottom = useCallback(() => {
        const questId = panel.filter?.questId
        if (questId) {
            panel.scrollToBottom()
            if (threadNewReplies === 0) {
                void queryClient
                    .invalidateQueries({
                        queryKey: ['quest', questId],
                        exact: true,
                    })
                    .then(() => {
                        if (String(panel.filter?.questId) === String(questId)) {
                            panel.scrollToBottom()
                        }
                    })
                    .catch(() => undefined)
            }
            return
        }

        // Browsers clamp an oversized destination to the scroll range. This avoids
        // reading scrollHeight solely to calculate the end of a non-virtualized feed.
        scrollContainerRef.current?.scrollTo({
            top: Number.MAX_SAFE_INTEGER,
            behavior: 'smooth',
        })
    }, [panel, threadNewReplies])

    // The feed's "N New" chip: beside the floating capsule, or with
    // fixed_header centred just under the panel's header.
    const newItemsPill = (
        <NewItemsPill
            source={isNotificationPanel ? 'notifications' : isOrdinalsPanel ? 'ordinals' : 'feed'}
            count={
                isNotificationPanel
                    ? numNoti
                    : isOrdinalsPanel
                    ? ordinalUpdatesCount
                    : newItemsCount
            }
            capped={
                isOrdinalsPanel ? ordinalUpdatesCount > 20 : !isNotificationPanel && newItemsCapped
            }
            authors={isNotificationPanel || isOrdinalsPanel ? [] : newItemsAuthors}
            active={
                isNotificationPanel
                    ? hasNotificationNewItems
                    : isOrdinalsPanel
                    ? hasOrdinalUpdates
                    : hasNewItems && !isMusicListTab
            }
        />
    )

    const handleFloatingFilterClick = useCallback(() => {
        if (hasNotificationNewItems) {
            const activeSpaceId = useStore.getState().activeSpaceId
            useStore.getState().set(state => consumeSpaceNotificationBadge(state, activeSpaceId))
        }
        const refreshTriggered = panel.triggerStreamRefresh?.({ smoothScroll: false }) ?? false
        if (!refreshTriggered) {
            panel.savedScrollTop = 0
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0
            }
        }
    }, [hasNotificationNewItems, panel])

    useLayoutEffect(
        function performTransform() {
            if (isMobile || onePanel || panelFrame || !panelContainerRef.current) return

            const transformContainer =
                panelContainerRef.current?.querySelector<HTMLElement>('.transform-container')
            const scrollContainer =
                panelContainerRef.current?.querySelector<HTMLDivElement>('.scroll-container')
            if (!transformContainer || !scrollContainer) return

            // Batch all reads
            const reads = () => {
                const centerIndex = getCenterIndex()
                const transformObject = getTransform(transformContainer.style.transform)
                const currentScale = transformObject.scale || 1
                return { centerIndex, currentScale }
            }

            // Batch all writes
            const writes = ({ centerIndex, currentScale }) => {
                if (centerIndex === index && currentScale === SMALL) {
                    transformTo(BIG, panel)
                } else if (centerIndex !== index && currentScale === BIG) {
                    transformTo(SMALL, panel)
                }
            }

            // Schedule reads then writes
            const frame = requestAnimationFrame(() => {
                const measurements = reads()
                writes(measurements)
            })

            return () => cancelAnimationFrame(frame)
        },
        [index, onePanel, panelFrame],
    )

    // Targeted fix: when inserting to the right from the left panel (3-up),
    // the previous center becomes right and can briefly render at scale(1).
    // Pre-apply SMALL for that specific panel on the initial re-render.
    useLayoutEffect(() => {
        if (isMobile || onePanel || panelFrame || !panelContainerRef.current) return
        const lastAction: PanelLastAction = useStore.getState().panels.lastAction
        if (!lastAction?.insert || !lastAction?.openRightFromLeft) {
            dbg('targeted-prepaint-skip', { reason: 'no matching lastAction', lastAction })
            return
        }
        // If this panel was the previous center, and is no longer center now, ensure SMALL pre-paint
        if (lastAction.prevCenterPanelId === panel.panelId && !isPanelCenter(panel.panelId)) {
            const transformContainer = panelContainerRef.current.querySelector(
                '.transform-container',
            ) as HTMLElement
            if (transformContainer) {
                dbg('targeted-prepaint-apply', { panelId: panel.panelId })
                transformContainer.style.transition = 'none'
                // Only adjust scale if it's currently 1 to avoid interfering with other animations
                const { scale } = getTransform(transformContainer.style.transform)
                if (!scale || Math.abs(scale - 1) < 0.01) {
                    transformContainer.style.transform = `scale(${SMALL})`
                }
                // Let the normal animation pipeline correct translateY next frame
                requestAnimationFrame(() => {
                    transformTo(SMALL, panel, 'none')
                })
            } else {
                dbg('targeted-prepaint-miss-transformContainer', { panelId: panel.panelId })
            }
        } else {
            dbg('targeted-prepaint-noop', {
                panelId: panel.panelId,
                prevCenterPanelId: lastAction.prevCenterPanelId,
                isCenterNow: isPanelCenter(panel.panelId),
            })
        }
    }, [panel.panelId, onePanel, lastAction])

    // We  memoize the panel context so it does not cause re-renders when passed to the provider below.
    const panelContext = React.useMemo(
        () => ({
            panel,
            panelId: panel.panelId,
            panelIndex: index,
            filter: panel.filter,
            hide: effectiveHide,
            panelContentActive,
            panelContentPrewarming,
            isCenter: () => isPanelCenter(panel.panelId),
            panelContainerRef,
            scrollContainerRef,
            revealPendingOpenPlacement,
            failPendingOpenPlacement,
            offerAnswerJump,
            setExternalHideBottomChip,
            setBottomEditorMounted,
            bottomEditorHost,
            measureBottomEditorField,
            // The software keyboard is up. A phone's composer pill folds with
            // the keyboard's departure rather than ahead of it (NewAnswer).
            keyboardOpen: Boolean(keyboardOpen),
            // bottom_editor_dock places this panel's pill, and its keyboardOpen
            // is the dock's (lib/bottomEditorDock).
            bottomEditorDocked: docked,
            setIsRefreshing: setIsRefreshingFromHeader,
            isRefreshing,
            setIsInitialLoading,
            isInitialLoading,
            refreshSource,
        }),
        [
            index,
            panel.panelId,
            panel.filter,
            effectiveHide,
            isRefreshing,
            isInitialLoading,
            refreshSource,
            setIsRefreshingFromHeader,
            panelContentActive,
            panelContentPrewarming,
            revealPendingOpenPlacement,
            failPendingOpenPlacement,
            offerAnswerJump,
            setBottomEditorMounted,
            bottomEditorHost,
            measureBottomEditorField,
            keyboardOpen,
            docked,
        ],
    )

    // The blank run under the content, and the same offset the bottom editor's
    // host keeps from the panel's edge, so the pill and the thread's end agree.
    const bottomSpacers = resolvePanelBottomSpacers({
        // Under the dock the foot keeps its resting spacer; the dock moves the
        // pill and the keyboard never resizes the thread's end.
        keyboardOpen: docked ? false : keyboardOpen,
        keyboardHeight,
        nativeKeyboardResize,
        isIOS,
        isMobile: bottomEditorMobileSurface,
        bottomEditorInset,
        bottomNavigationVisible,
    })
    const panelContainerStyle = {
        width: '100%',
        visibility: desktopPanelParked || hidePanelPrewarm ? 'hidden' : 'visible',
        '--legacy-panel-bottom-spacer': bottomSpacers.legacy,
        // fixed_header frames end the column at the frame's own edge: no stand-off
        // below it (the pill's margin is the bottom editor panel's padding).
        '--bottom-editor-panel-bottom-spacer':
            panelFrame && !isMobile ? '0px' : bottomSpacers.bottomEditor,
        '--bottom-editor-host-scale': bottomEditorHostScale,
        // What the pill clears with the navbar gone (fixed_header phones hide the
        // navbar on scroll; the pill drops to this, panel-container.module.scss).
        '--bottom-editor-inset': bottomEditorInset,
    } as React.CSSProperties

    // Clear transforms when in onePanel mode, or once fixed_header frames take
    // over (a side-panel scale written before the flag was known, or before the
    // centre settled on load, would otherwise stay on the content).
    useEffect(() => {
        if ((isMobile || onePanel || panelFrame) && panelContainerRef.current) {
            const transformContainer = panelContainerRef.current.querySelector(
                '.transform-container',
            ) as HTMLElement
            if (transformContainer) {
                transformContainer.style.transform = ''
                transformContainer.style.transition = ''
            }
        }
    }, [onePanel, panelFrame, index])

    // Restore only at the lifecycle boundary where desktop panel geometry changes.
    // The browser owns the live offset during scrolling; no listener mirrors it into JS.
    useLayoutEffect(() => {
        if (isMobile || desktopPanelParked) return
        const desiredTop = panel.savedScrollTop || 0
        const restoreFrame = requestAnimationFrame(() => {
            const sc = scrollContainerRef.current
            if (sc && desiredTop > 0) sc.scrollTop = desiredTop
        })

        return () => {
            cancelAnimationFrame(restoreFrame)
        }
    }, [desktopPanelParked, numPanels, panel.panelId])

    return (
        <PanelContextProvider value={panelContext}>
            <div
                style={panelContainerStyle}
                className={cn(
                    isMobile && 'is-mobile',
                    styles.panelContainer,
                    !isMobile &&
                        numPanels === 3 &&
                        showSidebar &&
                        isPanelLeft(panel.panelId) &&
                        styles.leftPanel,
                    'panel-container-comp',
                )}
                data-panel-theme={panelTheme}
                data-panel-content-active={panelContentActive ? 'true' : 'false'}
                data-panel-parked={desktopPanelParked ? 'true' : 'false'}
                data-panel-prewarming={panelContentPrewarming ? 'true' : 'false'}
                data-bottom-editor-mounted={bottomEditorMounted ? 'true' : undefined}
                data-jump-chip-visible={
                    isThreadPanel && (chipWouldShow || offerActive) ? 'true' : undefined
                }
                // fixed_header, desktop thread: the place bar caps the thread
                // column, which gives up its own rounded top under it.
                data-panel-frame={panelFrame && !panel.empty ? panelFrame : undefined}
                data-panel-kind={panelKind}
                data-fixed-header={fixedHeaderEnabled() ? 'true' : undefined}
                // Channels and new threads: the cards draw their own side
                // edges and the frame's own edge steps back.
                data-panel-card-edges={
                    panelKind === 'stream' || panelKind === 'new-thread' ? 'true' : undefined
                }
                data-panel-centre={panelFrame && isCentrePanel ? 'true' : undefined}
                data-panel-frame-settled={framedWindow && frameSettled ? 'true' : undefined}
                data-panel-side={panelFrame === 'window' && !isFocusedPanel ? 'true' : undefined}
                data-bottom-editor-dock={docked ? 'true' : undefined}
                ref={useCallback(
                    ref => {
                        if (!ref) {
                            return
                        }

                        // Capture previous scrollTop from the old scroll-container if it exists
                        const prevPanelRef = panel.getPanelRef()
                        if (prevPanelRef && prevPanelRef !== ref) {
                            try {
                                const prevScrollContainer = prevPanelRef.querySelector(
                                    '.scroll-container',
                                ) as HTMLElement
                                if (prevScrollContainer) {
                                    try {
                                        const previousScrollTop = prevScrollContainer.scrollTop || 0
                                        const latestPanel = useStore
                                            .getState()
                                            .panels.getPanel(panel.panelId)
                                        if (latestPanel)
                                            latestPanel.savedScrollTop = previousScrollTop
                                    } catch {}
                                }
                            } catch {}
                        }

                        panelContainerRef.current = ref
                        if (prevPanelRef !== ref) {
                            panel.setPanelRef(ref)
                        }
                    },
                    [panel],
                )}
            >
                {isMobile ? (
                    <MobilePullToRefresh
                        scrollContainerRef={scrollContainerRef}
                        onRefresh={refreshFromGesture}
                        isRefreshing={gestureIsRefreshing}
                        filter={panel.filter}
                        showSpinner={!panel.empty}
                        disabled={Boolean(useStore.getState().anyMenuOpen)}
                    />
                ) : (
                    <PullToRefresh
                        scrollContainerRef={scrollContainerRef}
                        onRefresh={refreshFromGesture}
                        isRefreshing={gestureIsRefreshing}
                        filter={panel.filter}
                        showSpinner={!panel.empty}
                    />
                )}

                {/* fixed_header: the header row carries the capsule's scope
                    icon, click and new-items chip instead. */}
                {!panel.empty && !fixedHeaderEnabled() && (
                    <WithTooltip
                        asChild
                        tip={isThreadPanel ? 'Scroll to top.' : 'Refresh or scroll to top.'}
                    >
                        <div
                            ref={floatingFilterRef}
                            data-testid="panel-floating-filter-pill"
                            data-panel-scroll-top-obstruction="true"
                            className={cn(styles.floatingFilterContainer)}
                            onClick={handleFloatingFilterClick}
                        >
                            {/* The capsule pill clips its icons (overflow: hidden),
                                so the halo ring lives on this wrapper instead of a
                                negative-inset pseudo-element on the pill itself. */}
                            <div
                                className={cn(
                                    styles.capsuleHaloWrap,
                                    capsuleCarriesRefreshHalo && styles.refreshing,
                                )}
                            >
                                <FloatingFilterIcon panel={panel} />
                            </div>
                            {/* Separate affordance beside the capsule: grows out
                                when unseen items wait above; the shared centered
                                container makes the capsule slide aside. Clicks
                                fall through to this container's handler. */}
                            {!isThreadPanel && newItemsPill}
                        </div>
                    </WithTooltip>
                )}

                {/* Floating header slide controls (desktop). fixed_header puts
                    back/forward in the panel's header row instead. */}
                {!isMobile &&
                    !showPlaceBar &&
                    isPanelCenter(panel.panelId) &&
                    (panelsLength || 0) > 1 && (
                        <>
                            <WithTooltip asChild tip="Slide left.">
                                <div
                                    ref={floatingHeaderNavLeftRef}
                                    className={cn(styles.floatingHeaderNavLeft)}
                                    style={{ left: onePanel ? 0 : 6 }}
                                    onClick={slidePrev}
                                >
                                    <ArrowLeft size={24} />
                                </div>
                            </WithTooltip>
                            <WithTooltip asChild tip="Slide right.">
                                <div
                                    ref={floatingHeaderNavRightRef}
                                    className={cn(styles.floatingHeaderNavRight)}
                                    style={{ right: onePanel ? 0 : 6 }}
                                    onClick={slideNext}
                                >
                                    <ArrowRight size={24} />
                                </div>
                            </WithTooltip>
                        </>
                    )}

                {hideForPendingOpenPlacement && (
                    <PanelLoadingIndicator
                        label="Loading post"
                        error={
                            effectiveOpenPlacementPaintGate.failure
                                ? effectiveOpenPlacementPaintGate.failure === 'unavailable'
                                    ? 'Post unavailable.'
                                    : 'Could not load this post.'
                                : undefined
                        }
                        onRetry={retryPendingOpenPlacement}
                    />
                )}
                <div
                    ref={setScrollContainerRef}
                    data-testid="panel-scroll-container"
                    data-panel-id={panel.panelId}
                    data-panel-index={index}
                    data-open-placement-pending={hideForPendingOpenPlacement ? 'true' : undefined}
                    className={cn(
                        'scroll-container',
                        styles.scrollContainer,
                        naturalThreadLayout && styles.naturalThreadScrollContainer,
                        'h100',
                        !isMobile && styles.onePanelScrollContainer,
                    )}
                    style={{
                        display: desktopPanelParked ? 'none' : 'block',
                    }}
                >
                    <div
                        ref={scrollTopSentinelRef}
                        data-testid="panel-scroll-top-sentinel"
                        className={styles.scrollTopSentinel}
                        aria-hidden="true"
                    />
                    <div
                        ref={transformContainerRef}
                        {...(hideForPendingOpenPlacement ? { inert: '' } : {})}
                        aria-hidden={hideForPendingOpenPlacement || undefined}
                        className={cn(
                            styles.transformContainer,
                            shouldUseTransformLayer && styles.transformLayer,
                            `panel-${panel.panelId}`,
                            'transform-container h100',
                            isMobile && 'is-mobile',
                        )}
                        style={
                            hideForPendingOpenPlacement ? OPEN_PLACEMENT_HIDDEN_STYLE : undefined
                        }
                    >
                        {children}
                        {/* Last in-flow content child: thread rows are natural-flow,
                            so this lands at true content bottom. A sibling outside
                            would sit at the container's fixed 100dvh instead. Only
                            the empty gutter below may follow it — "at bottom" must
                            mean the content is visible, not that the user scrolled
                            through blank space. */}
                        {isThreadPanel && (
                            <div
                                ref={scrollBottomSentinelRef}
                                data-testid="panel-scroll-bottom-sentinel"
                                className={styles.scrollBottomSentinel}
                                aria-hidden="true"
                            />
                        )}
                        {isThreadPanel && (
                            <div
                                ref={threadBottomGutterRef}
                                data-testid="panel-thread-bottom-gutter"
                                data-thread-bottom-gutter="true"
                                className={cn(
                                    styles.threadBottomGutter,
                                    keyboardOpen && !docked && styles.threadBottomGutterCollapsed,
                                )}
                                aria-hidden="true"
                            />
                        )}
                    </div>
                </div>

                {/* fixed_header: the place bar sits over the top of the scroll
                    area, outside the scroller, so it stays put while posts
                    scroll under it and while pull-to-refresh moves the feed
                    (the void opens below it). Panel.jsx keeps the 46px spacer
                    it covers. */}
                {showPlaceBar && (
                    <PanelPlaceBar
                        filter={panel.filter}
                        navigable={isFocusedPanel}
                        frame={panelFrame}
                        onHeadClick={handleFloatingFilterClick}
                        newItems={isThreadPanel ? undefined : newItemsPill}
                    />
                )}

                {/* bottom_editor: the thread portals its composer here, fixed to
                    the panel's foot at every scroll position. The host clears the
                    phone's bottom inset (or the keyboard) by the same spacer the
                    content reserves below the card. */}
                {isThreadPanel && bottomEditorMounted && (
                    <div
                        ref={attachBottomEditorHost}
                        data-testid="panel-bottom-editor-host"
                        className={cn(
                            styles.bottomEditorHost,
                            !isMobile && styles.bottomEditorHostDesktop,
                            docked && styles.bottomEditorHostDock,
                            isAndroid && styles.bottomEditorHostAndroid,
                        )}
                        // The margin under the pill stops at a navbar standing
                        // under it, except on Android, where its blur carries on
                        // behind the navbar to the edge (panel-container.module.scss).
                        data-bottom-editor-navigation={bottomNavigationVisible ? 'true' : undefined}
                        style={{
                            visibility: hideForPendingOpenPlacement ? 'hidden' : undefined,
                        }}
                    >
                        <div ref={attachBottomEditorGlide} className={styles.bottomEditorGlide} />
                    </div>
                )}

                {/* One capsule, two states. The jump-to-latest chip and the
                    post-reply offer were separate elements that suppressed each
                    other; as one element they cannot stack, and the offer can
                    grow out of the chip instead of appearing over it. */}
                {(isThreadPanel || offerActive) && (
                    <div
                        data-testid="panel-jump-latest-chip"
                        data-panel-id={panel.panelId}
                        data-panel-index={index}
                        data-control-state={offerActive ? 'offer' : 'chip'}
                        data-offer-kind={offerActive ? answerJumpOffer?.kind : undefined}
                        className={cn(
                            styles.threadBottomChipContainer,
                            (chipWouldShow || offerActive) && styles.isVisible,
                            shouldLiftBottomControls && styles.centerThreePanelBottomChipOffset,
                            bottomPillEdgeClass,
                        )}
                    >
                        <WithTooltip asChild disable={offerActive} tip="Jump to latest.">
                            <button
                                type="button"
                                data-testid="panel-jump-latest-button"
                                aria-label={offerActive ? offerLabel : 'Jump to latest'}
                                className={cn(
                                    styles.threadBottomChip,
                                    offerActive && styles.threadBottomChipOffered,
                                    offerActive &&
                                        answerJumpOffer?.kind === 'open' &&
                                        styles.threadBottomChipOpenKind,
                                    // No chip was on screen to grow from, so the
                                    // pill rises from the edge on a keyframe.
                                    offerActive &&
                                        answerJumpOffer?.fromHidden &&
                                        !answerJumpRetreating &&
                                        styles.threadJumpOfferArriving,
                                    // Nothing to settle back into: leave the way
                                    // it arrived rather than shrinking into air.
                                    answerJumpRetreating &&
                                        !chipWouldShow &&
                                        styles.threadJumpOfferRetreating,
                                )}
                                onClick={offerActive ? takeAnswerJump : handleScrollToBottom}
                            >
                                {/* The count belongs to useThreadNewReplies and can
                                    legitimately be > 0 while the pill is up, so the
                                    track collapses without touching the state. */}
                                <span
                                    className={cn(
                                        styles.newRepliesPillWrap,
                                        threadNewReplies > 0 && !offerActive && styles.isOpen,
                                    )}
                                >
                                    <span className={styles.newRepliesPillClip}>
                                        <span
                                            data-testid={
                                                threadNewReplies > 0
                                                    ? 'thread-new-replies-count'
                                                    : undefined
                                            }
                                            className={styles.newRepliesPill}
                                        >
                                            {threadNewReplies > 20 ? '20+' : threadNewReplies || ''}
                                        </span>
                                    </span>
                                </span>
                                <svg
                                    className={styles.threadBottomChipIcon}
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    {/* The chip is a bare chevron; the pill grows
                                        the arrow's stem in with it. */}
                                    <path className={styles.threadBottomChipStem} d="M12 5v14" />
                                    <path d="m19 12-7 7-7-7" />
                                </svg>
                                <span
                                    className={cn(
                                        styles.threadBottomChipLabelWrap,
                                        offerActive && styles.isOpen,
                                    )}
                                >
                                    <span className={styles.threadBottomChipLabelClip}>
                                        <span className={styles.threadBottomChipLabel}>
                                            {offerLabel}
                                        </span>
                                    </span>
                                </span>
                            </button>
                        </WithTooltip>
                    </div>
                )}

                {!panel.empty && panel.filter && !isMobile && (
                    <RemoveButton
                        panel={panel}
                        index={index}
                        containerRef={removeButtonContainerRef}
                        liftAboveBottomControls={shouldLiftBottomControls}
                    />
                )}
            </div>
        </PanelContextProvider>
    )
})
